
import React, { useState, useEffect } from 'react';
import { Phone, User, ShieldAlert, HeartHandshake, Mic2, Lock, Unlock, CreditCard, Eye } from 'lucide-react';

const SalesPlaybook: React.FC = () => {
  // Load access level from storage
  const [accessLevel, setAccessLevel] = useState<'locked' | 'demo' | 'full'>(() => {
      const stored = localStorage.getItem('orbit_playbook_access');
      return (stored as 'locked' | 'demo' | 'full') || 'locked';
  });
  
  const [activeTab, setActiveTab] = useState<'b2b' | 'gatekeeper' | 'objections'>('b2b');

  // Save access level to storage
  useEffect(() => {
      localStorage.setItem('orbit_playbook_access', accessLevel);
  }, [accessLevel]);

  // Gatekeeper Component
  if (accessLevel === 'locked') {
      return (
        <div className="h-full flex items-center justify-center animate-fade-in">
             <div className="bg-slate-800 border border-slate-700 p-8 rounded-2xl max-w-2xl w-full shadow-2xl relative overflow-hidden text-center">
                 <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-orange-500"></div>
                 
                 <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-slate-700">
                     <Lock className="w-10 h-10 text-red-500" />
                 </div>
                 
                 <h2 className="text-3xl font-bold text-white mb-2">Restricted Access</h2>
                 <p className="text-slate-400 mb-8">
                     Dieses "Script Bot" Modul enthält vertrauliche Verkaufspsychologie für die Akquise von Anwälten und Steuerberatern.
                 </p>

                 <div className="grid md:grid-cols-2 gap-6">
                     <button 
                        onClick={() => setAccessLevel('demo')}
                        className="bg-slate-700 hover:bg-slate-600 border border-slate-600 p-6 rounded-xl transition-all group hover:-translate-y-1"
                     >
                         <Eye className="w-8 h-8 text-blue-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                         <div className="font-bold text-white text-lg">Demo Modus</div>
                         <p className="text-xs text-slate-400 mt-2">RA-Akquise Script ansehen.</p>
                         <div className="mt-4 text-xs font-bold text-blue-400 uppercase tracking-wider">Kostenlos</div>
                     </button>

                     <button 
                         onClick={() => setAccessLevel('full')}
                         className="bg-gradient-to-br from-blue-900 to-slate-900 hover:from-blue-800 hover:to-slate-800 border border-blue-500/50 p-6 rounded-xl transition-all group hover:-translate-y-1 relative"
                     >
                         <div className="absolute top-3 right-3 bg-blue-600 text-[10px] font-bold px-2 py-0.5 rounded text-white">RECOMMENDED</div>
                         <CreditCard className="w-8 h-8 text-emerald-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                         <div className="font-bold text-white text-lg">Full Access</div>
                         <p className="text-xs text-slate-400 mt-2">Inkl. Kammer-Einwände & Gatekeeper.</p>
                         <div className="mt-4 text-xs font-bold text-emerald-400 uppercase tracking-wider">Kunde werden</div>
                     </button>
                 </div>
             </div>
        </div>
      );
  }

  return (
    <div className="animate-fade-in h-full flex flex-col">
      <header className="mb-6 flex justify-between items-center">
        <div>
            <h2 className="text-3xl font-bold text-white tracking-tight">Sales Playbook: Legal</h2>
            <p className="text-slate-400">Strategien für Anwälte & Steuerberater</p>
        </div>
        <div className="flex items-center space-x-2">
            {accessLevel === 'demo' ? (
                <div className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-xs font-bold border border-yellow-500/30 flex items-center">
                    <Eye className="w-3 h-3 mr-1" /> Demo Mode
                </div>
            ) : (
                <div className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs font-bold border border-emerald-500/30 flex items-center">
                    <Unlock className="w-3 h-3 mr-1" /> Full Access
                </div>
            )}
            <button onClick={() => setAccessLevel('locked')} className="bg-slate-800 p-2 rounded hover:bg-slate-700 text-slate-400">
                <Lock className="w-4 h-4" />
            </button>
        </div>
      </header>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('b2b')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'b2b' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          <Phone className="w-4 h-4 mr-2" />
          RA/StB Pitch (Chef)
        </button>
        
        <button
          onClick={() => accessLevel === 'full' ? setActiveTab('gatekeeper') : alert("🔒 Upgrade to Full Access")}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
            activeTab === 'gatekeeper' 
                ? 'bg-purple-600 text-white' 
                : accessLevel === 'demo' ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          <User className="w-4 h-4 mr-2" />
          Das Vorzimmer (Sekretariat)
          {accessLevel === 'demo' && <Lock className="w-3 h-3 ml-2 text-slate-600" />}
        </button>

        <button
          onClick={() => accessLevel === 'full' ? setActiveTab('objections') : alert("🔒 Upgrade to Full Access")}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
            activeTab === 'objections' 
                ? 'bg-orange-600 text-white' 
                : accessLevel === 'demo' ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          <ShieldAlert className="w-4 h-4 mr-2" />
          Einwandbehandlung (Kammer/DSGVO)
          {accessLevel === 'demo' && <Lock className="w-3 h-3 ml-2 text-slate-600" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-slate-800 border border-slate-700 rounded-xl p-8 shadow-lg">
        
        {activeTab === 'b2b' && (
          <div className="prose prose-invert max-w-none">
            <h3 className="text-xl font-bold text-blue-400 mb-4">Phase 1: Der "Zeitfresser" Einstieg</h3>
            <div className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
              <p className="font-mono text-sm text-slate-300">
                "Grüß Gott Herr Magister [Name], hier ist [Dein Name] von ARKORBIT AI Austria. Ich fasse mich kurz: Wir helfen Kanzleien, die nicht verrechenbare Zeit für Aktenvermerke und Protokolle um 90% zu reduzieren. Diktieren Sie noch klassisch aufs Band, oder nutzen Sie schon KI-gestützte Systeme?"
              </p>
            </div>

            <h3 className="text-xl font-bold text-blue-400 mb-4">Phase 2: Die Problem-Awareness</h3>
            <div className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
              <p className="font-mono text-sm text-slate-300 mb-2">
                "Das Problem kennen Sie ja: Sie kommen vom Termin, diktieren 5 Minuten ins Band. Ihre Assistenz tippt 15 Minuten. Dann korrigieren Sie nochmal 5 Minuten. Das sind 25 Minuten Arbeitszeit für ein Blatt Papier."
              </p>
              <p className="font-mono text-sm text-slate-300">
                "Unsere Lösung generiert das fertige PDF, formatiert und gegliedert, noch während Sie im Auto sitzen. Und das Wichtigste: Die Daten verlassen dabei nie Ihre eigene Infrastruktur. Es ist so sicher wie Ihr eigener Aktenschrank."
              </p>
            </div>

            <h3 className="text-xl font-bold text-blue-400 mb-4">Phase 3: Der DSGVO Close</h3>
            <div className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-emerald-500 mb-6">
              <p className="font-mono text-sm text-slate-300">
                "Herr Magister, ich schicke Ihnen einen Link. Diktieren Sie irgendeinen fiktiven Fall hinein. Sie werden sehen: Das Ergebnis ist in 30 Sekunden da. Wenn Sie die Qualität überzeugt, sprechen wir über die Integration in Ihr BMD oder RZL System. Einverstanden?"
              </p>
            </div>
          </div>
        )}

        {activeTab === 'gatekeeper' && (
          <div className="prose prose-invert max-w-none animate-fade-in">
            <div className="flex items-center mb-4">
               <span className="bg-purple-500/20 text-purple-300 text-xs font-bold px-2 py-1 rounded uppercase tracking-wider mr-2">Strategie</span>
               <span className="text-slate-400 text-sm">Das Sekretariat als Verbündeten gewinnen (Entlastung!).</span>
            </div>

            <h3 className="text-xl font-bold text-purple-400 mb-4">Einstieg: "Ich will Sie entlasten"</h3>
            <div className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-purple-500 mb-6">
              <p className="font-mono text-sm text-slate-300">
                "Grüß Gott Frau [Name], [Dein Name] hier. Ich habe eine etwas ungewöhnliche Frage: Wie viel Zeit verbringen Sie pro Woche damit, Bänder abzutippen? ... Verstehe. Wäre es für Sie eine Erleichterung, wenn die Erstfassung dieser Texte schon fertig formatiert auf Ihrem Bildschirm erscheint und Sie nur noch kurz Korrektur lesen müssten?"
              </p>
            </div>

            <h3 className="text-xl font-bold text-purple-400 mb-4">Der Nutzen für die Assistenz</h3>
            <div className="bg-slate-900/50 p-4 rounded-lg border-l-4 border-purple-500 mb-6">
              <p className="font-mono text-sm text-slate-300">
                "Wir haben eine Software, die genau das macht. Das Ziel ist nicht, Sie zu ersetzen – Gott bewahre, gute Assistentinnen sind unersetzbar! – sondern Ihnen den langweiligen Teil der Arbeit abzunehmen, damit Sie sich um die Mandanten kümmern können. Glauben Sie, Ihr Chef wäre offen dafür, wenn Sie ihm vorschlagen, dass wir das mal testen?"
              </p>
            </div>
          </div>
        )}

        {activeTab === 'objections' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
            <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                <h4 className="font-bold text-red-400 mb-2">"Datenschutz / Cloud ist unsicher."</h4>
                <p className="text-sm text-slate-300 mb-2">Antwort:</p>
                <div className="bg-slate-800 p-3 rounded text-sm italic text-slate-400">
                    "Absolut valider Punkt. Genau deshalb nutzen wir keine öffentliche Cloud für die Speicherung. Wir installieren einen dedizierten Server für Sie in Frankfurt (Hostinger Enterprise). Die Daten gehören physisch Ihnen. Nach der Transkription löscht sich die Audiodatei sofort unwiderruflich."
                </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                <h4 className="font-bold text-red-400 mb-2">"Wir nutzen BMD / RZL / Advokat."</h4>
                <p className="text-sm text-slate-300 mb-2">Antwort:</p>
                <div className="bg-slate-800 p-3 rounded text-sm italic text-slate-400">
                    "Perfekt. Wir ersetzen Ihre Kanzleisoftware nicht, wir füttern sie. Unser System erstellt PDFs oder strukturierte Daten, die Sie einfach per Drag & Drop in den Akt ziehen oder über unsere Schnittstelle direkt importieren können."
                </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                <h4 className="font-bold text-red-400 mb-2">"KI halluziniert / macht Fehler."</h4>
                <p className="text-sm text-slate-300 mb-2">Antwort:</p>
                <div className="bg-slate-800 p-3 rounded text-sm italic text-slate-400">
                    "Stimmt bei ChatGPT. Wir nutzen aber 'Grounding'. Das System darf nur das protokollieren, was gesagt wurde. Es dichtet nichts dazu. Außerdem ist es eine 'Drafting'-Lösung: Die anwaltliche Prüfung bleibt natürlich bei Ihnen, aber Sie starten bei 95% Fertigstellung statt bei 0%."
                </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                <h4 className="font-bold text-red-400 mb-2">"Haben keine Zeit für IT-Projekte."</h4>
                <p className="text-sm text-slate-300 mb-2">Antwort:</p>
                <div className="bg-slate-800 p-3 rounded text-sm italic text-slate-400">
                    "Das ist der beste Teil: Es gibt kein IT-Projekt bei Ihnen. Wir stellen Ihnen den Zugang bereit. Sie loggen sich ein und diktieren los. Keine Installation auf Ihren Kanzlei-Rechnern notwendig, wenn Sie nicht wollen."
                </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SalesPlaybook;
